import React, { useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Card, InputNumber, Space, Spin, Table } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { WorkflowInvoice } from '../../../types/workflowInvoice';
import { formatUnit } from '../../../utils/units';
import { useFeedback } from '../../../utils/feedback';
import { fetchPurchaseReceiptPreview, receivePurchaseInvoice, type PurchaseReceiptLine, type InvoiceApprovalPreview as Preview } from '../api/invoiceWorkflowApi';
import InvoiceApprovalPreview from './InvoiceApprovalPreview';
import { submitWithOperationalException } from '../../operationalExceptions/operationalExceptionFlow';

export default function PurchaseInvoiceReceipt({ invoice, preview, onChanged }: { invoice: WorkflowInvoice; preview: Preview | null; onChanged: () => void }) {
  const { t } = useTranslation(['invoices','common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [lines,setLines] = useState<PurchaseReceiptLine[] | null>(null);
  const [failed,setFailed] = useState(false);
  const [busy,setBusy] = useState(false);
  const [revision,setRevision] = useState(0);
  const [quantities,setQuantities] = useState<Record<string,number | null>>({});
  const [selections,setSelections] = useState<Array<{ purchaseInvoiceItemId: string; pendingIssueIds: string[]; reason?: string }>>([]);
  const [documents,setDocuments] = useState<Array<{id:string;pzNumber:string}>>([]);
  const key = useRef<string | null>(null);
  const text = (name: string) => t(`invoices:workflow.physicalReceipt.${name}`);
  useEffect(() => {
    let active = true;
    setFailed(false); setLines(null);
    fetchPurchaseReceiptPreview(invoice.id!).then(result => { if (active) { setLines(result.lines); setDocuments(result.pzDocuments); } })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [invoice.id,revision]);
  const receive = async () => {
    setBusy(true);
    key.current ??= `receipt-${invoice.id}-${Date.now()}`;
    try {
      const input = { expectedVersion: invoice.version!, idempotencyKey: key.current,
        recognitionMonth: dayjs().format('YYYY-MM'), pendingStockSelections: selections,
        receiptQuantities: (lines || []).filter(line => (quantities[line.purchaseInvoiceItemId] || 0)>0)
          .map(line => ({purchaseInvoiceItemId:line.purchaseInvoiceItemId,quantity:quantities[line.purchaseInvoiceItemId]!})) };
      const result = await submitWithOperationalException(payload => receivePurchaseInvoice(invoice.id!, payload as typeof input), input, modal, t);
      if (!result) return;
      setDocuments(previous => [...previous,...result.pzDocuments]); setQuantities({}); setSelections([]); key.current=null;
      setRevision(value=>value+1); onChanged(); feedback.success(text('saved'));
    } catch (error: any) { feedback.error(error?.response?.data?.error || text('failed')); }
    finally { setBusy(false); }
  };
  return <Card title={text('title')}><Space direction="vertical" style={{width:'100%'}}>
    <p>{text('hint')}</p>
    {failed ? <Alert type="error" message={text('failed')} action={<Button onClick={()=>setRevision(value=>value+1)}>{t('common:refresh')}</Button>} /> : !lines ? <Spin /> : <>
      {lines.some(line=>line.remainingQuantity>0) ? <>
        <Table<PurchaseReceiptLine> rowKey="purchaseInvoiceItemId" pagination={false} dataSource={lines.filter(line=>line.remainingQuantity>0)} columns={[
          {title:text('product'),dataIndex:'productName'},
          {title:text('remaining'),render:(_,line)=>`${line.remainingQuantity} ${formatUnit(line.unit)}`},
          {title:text('quantity'),render:(_,line)=><InputNumber aria-label={`${text('quantity')} ${line.productName}`} min={0} max={line.remainingQuantity} precision={3} disabled={busy} value={quantities[line.purchaseInvoiceItemId]} onChange={value=>{key.current=null;setQuantities(previous=>({...previous,[line.purchaseInvoiceItemId]:value}));}} />},
        ]} />
        {preview && preview.pendingStockCandidates.some(group=>group.candidates.length>0) && <InvoiceApprovalPreview invoice={invoice} preview={preview} selections={selections} onChange={value=>{key.current=null;setSelections(value);}} disabled={busy} />}
        <Button type="primary" loading={busy} disabled={!lines.some(line=>(quantities[line.purchaseInvoiceItemId]||0)>0)} onClick={()=>void receive()}>{text('receive')}</Button>
      </> : <Alert type="success" message={text('complete')} />}
    </>}
    {documents.map(document=><Link key={document.id} to={`/app/pz/${document.id}`}>{document.pzNumber}</Link>)}
  </Space></Card>;
}
